import { Navigate } from 'react-router-dom'

import { useAuth } from '../contexts/AuthContext'
import { isOurApp } from '../lib/platform'

/**
 * What the app opens on.
 *
 * Capacitor's `server.url` is the bare origin, so every cold start loads `/`
 * — and `/` is the marketing landing page, which knows nothing about whether
 * anyone is signed in. Reopening the app after swiping it away therefore
 * showed a page headed by Sign In buttons, no matter how healthy the session
 * was. Reported, over and over, as "when I open it again I'm logged out".
 *
 * The session was fine. The launch beacon caught it exactly: `token=True`,
 * immediately followed by the landing page's own `public-stats` call, and a
 * fresh sign-in three seconds later. Nothing was signing the user out; they
 * were being shown a page that looks signed out and reasonably believed it.
 *
 * Only in the app. On the website a signed-in visitor reaching the homepage is
 * ordinary and the marketing page is the right answer — the app is the case
 * where it is never the right answer, because it is the whole front door.
 */
export default function AppLanding({ landing }: { landing: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuth()

  // The session is restored in an effect, so the first render always looks
  // signed out. Redirecting on that render would send a signed-in user to the
  // marketing page every single launch — the bug, reintroduced from the other
  // side. Matches ProtectedRoute's treatment.
  if (loading && isOurApp()) {
    return (
      <div className="min-h-screen bg-neutral-950 flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    )
  }

  if (isOurApp()) {
    // Both branches, in one place, with the session actually in hand. App.tsx
    // used to send every launch to /login from an effect without checking —
    // which is the bug — but the marketing page really is the wrong front door
    // for the app, so signed out still goes there.
    return <Navigate to={isAuthenticated ? '/learning' : '/login'} replace />
  }

  return <>{landing}</>
}
